// @section 1 Campaigns / MasterDetail — drill from Overview, one master deep
// Shows aggregated KPIs, 30-day timeseries, and the list of child sends
// (design.campaigns rows under this master). Each child links to the
// existing wave-level CampaignDetail at /campaigns/:campaignId.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TrendChart, { type TrendMetric, type TrendRow } from '../../components/TrendChart';
import {
  getCampaignMasterDetail,
  getCampaignMasterDaily,
  CampaignsUnconfigured,
  type CampaignMasterDetail as CampaignMasterDetailType,
  type CampaignDailyActivity,
} from '../../api';

// @section 2 Chart metrics (true per-day activity for this master)
const BASE_FIELDS = ['sent', 'delivered', 'opens', 'demo', 'diag', 'booked', 'replies', 'engaged'];
const METRICS: TrendMetric[] = [
  { key: 'sent', label: 'Sent', kind: 'count', field: 'sent', color: '#14b8a6' },
  { key: 'opens', label: 'Opens', kind: 'count', field: 'opens', color: '#10b981' },
  { key: 'demo', label: 'Demo clicks', kind: 'count', field: 'demo', color: '#f59e0b' },
  { key: 'booked', label: 'Booked', kind: 'count', field: 'booked', color: '#f0606b' },
  { key: 'openRate', label: 'Open rate', kind: 'rate', num: 'opens', den: 'delivered', color: '#10b981' },
];

// @section 3 Main
export default function MasterDetail() {
  const { masterKey } = useParams<{ masterKey: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<CampaignMasterDetailType | null>(null);
  const [daily, setDaily] = useState<CampaignDailyActivity[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [unconfigured, setUnconfigured] = useState(false);

  // @subsection 3.1 Fetch on mount — detail (KPIs + children) + true daily activity
  useEffect(() => {
    if (!masterKey) return;
    setData(null); setDaily(null); setErr(null); setUnconfigured(false);
    const onErr = (e: unknown) => {
      if (e instanceof CampaignsUnconfigured) setUnconfigured(true); else setErr(String(e));
    };
    getCampaignMasterDetail(masterKey, 120).then(setData).catch(onErr);
    getCampaignMasterDaily(masterKey, 120).then(setDaily).catch(onErr);
  }, [masterKey]);

  // @subsection 3.2 Chart rows — true per-day activity (add derived `delivered`)
  const rows = useMemo<TrendRow[]>(() => {
    if (!daily) return [];
    return daily.map((d) => ({
      date: d.activity_date.slice(0, 10),
      sent: d.sent, bounced: d.bounced, delivered: Math.max(d.sent - d.bounced, 0),
      opens: d.opens, demo: d.demo_clicks, diag: d.diag_visits,
      engaged: d.replies_engaged, booked: d.booked, replies: d.replies,
    }));
  }, [daily]);

  const master = data?.master ?? null;
  const children = data?.children ?? [];

  return (
    <div className="md:-mx-10 md:-my-8">
      <section className="bg-ink-950 text-paper-100 min-h-screen font-sans">
        <div className="px-4 py-5 md:px-10 md:py-7 max-w-[1400px] mx-auto">

          {/* @subsection 3.3 Breadcrumb */}
          <div className="flex items-center justify-between text-xs mb-6">
            <div className="text-muted">
              <span className="text-paper-100">Atlas</span>
              <span className="opacity-40"> / </span>
              <button onClick={() => navigate('/campaigns')}
                      className="text-accent-400 hover:underline">Campaigns</button>
              <span className="opacity-40"> / </span>
              <span className="text-paper-100/80">{master?.master_name ?? masterKey}</span>
            </div>
            <button onClick={() => navigate('/campaigns')}
                    className="text-xs text-accent-400 hover:underline">← back to all campaigns</button>
          </div>

          {/* @subsection 3.4 Title + KPI strip */}
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-6 mb-8">
            <div className="flex-shrink-0">
              <div className="text-[11px] uppercase tracking-[3px] text-muted mb-2">
                {master?.vertical_id && master.vertical_id !== '—' ? master.vertical_id : 'Campaign'}
                {master?.master_status && master.master_status !== 'unassigned' && (
                  <span className="ml-2 text-muted">· {master.master_status}</span>
                )}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-paper-100 mb-2">
                {master?.master_name ?? '—'}
              </h1>
              <p className="text-sm text-muted md:whitespace-nowrap">
                {master ? `${fmt(master.child_campaign_count)} send${master.child_campaign_count === 1 ? '' : 's'} · snapshot ${shortTs(master.taken_at)}` : 'Loading…'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:flex-1">
              <Kpi label="Sent" value={master?.sent} tone="paper-100"
                help={master ? `${fmt(master.bounced)} bounced` : '—'} />
              <Kpi label="Opens" value={master?.unique_opens} tone="accent-400"
                help={master ? `${fmt(master.unique_clicks)} clicks` : '—'} />
              <Kpi label="Engaged replies" value={master?.replies_engaged} tone="ok"
                help={master ? `${fmt(master.unique_replies)} total replies` : '—'} />
              <Kpi label="Bookings" value={master?.bookings_count} tone="watch"
                help={master ? `${fmt(master.diag_questionnaire_filled)} diag filled` : '—'} />
            </div>
          </div>

          {/* @subsection 3.5 Error / unconfigured */}
          {err && (
            <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 mb-6 text-stall text-sm">{err}</div>
          )}
          {unconfigured && (
            <div className="rounded-xl border-2 border-watch/40 bg-ink-900 p-5 mb-6 text-sm text-watch">
              <div className="font-semibold mb-1">Campaigns pipeline isn't configured</div>
              <div className="text-muted">Backend returned 503. Awaiting first refresh.</div>
            </div>
          )}

          {/* @subsection 3.6 Body grid — trend left, child sends right */}
          <div className="grid gap-6 grid-cols-1 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">

            {/* LEFT — timeseries (ported SVG TrendChart, daily activity) */}
            <div>
              {rows.length >= 2 ? (
                <TrendChart data={rows} metrics={METRICS} baseFields={BASE_FIELDS} title="Daily activity" />
              ) : (
                <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4 text-muted text-xs py-12 text-center">
                  Not enough daily data for a trend yet.
                </div>
              )}

              {/* Reply classification + diag funnel — same shape as wave-level Detail */}
              {master && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <Panel title="✉️ Reply classification">
                    <ul className="space-y-1.5 text-xs">
                      <Row label="Engaged" value={master.replies_engaged} tone="text-ok" />
                      <Row label="Not interested" value={master.replies_not_interested} tone="text-stall" />
                      <Row label="Redirect" value={master.replies_redirect} tone="text-muted" />
                      <Row label="Auto · OOO" value={master.replies_auto_ooo} tone="text-muted" />
                      <Row label="Auto · other" value={master.replies_auto_other} tone="text-muted" />
                      <Row label="Unclassified" value={master.replies_unclassified} tone="text-muted" />
                    </ul>
                  </Panel>
                  <Panel title="🧪 Diag funnel">
                    <ul className="space-y-1.5 text-xs">
                      <Row label="Tokens invited" value={master.diag_tokens_invited} tone="text-muted" />
                      <Row label="Visited" value={master.diag_tokens_visited} tone="text-paper-100" />
                      <Row label="Real browser" value={master.diag_tokens_real_browser} tone="text-paper-100" />
                      <Row label="Filled" value={master.diag_questionnaire_filled} tone="text-ok" />
                      <Row label="First-clicked" value={master.diag_first_clicked_count} tone="text-accent-400" />
                    </ul>
                  </Panel>
                </div>
              )}
            </div>

            {/* RIGHT — child sends list */}
            <div>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-2xl font-bold text-paper-100">📤 Sends</h2>
                <span className="text-xs text-muted">{children.length} · click to drill</span>
              </div>
              <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-3 overflow-x-auto">
                {children.length === 0 ? (
                  <div className="text-muted text-xs py-6 text-center">No child sends yet.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-muted text-left">
                        <th className="py-1.5">Send</th>
                        <th className="text-right">Sent</th>
                        <th className="text-right">Engaged</th>
                        <th className="text-right">Book</th>
                      </tr>
                    </thead>
                    <tbody>
                      {children.map((c) => (
                        <tr key={c.campaign_id}
                            className="border-t border-ink-800 cursor-pointer hover:bg-ink-800/40"
                            onClick={() => navigate(`/campaigns/${c.campaign_id}`)}>
                          <td className="py-1.5 text-paper-100 max-w-[220px]">
                            <div className="truncate" title={c.campaign_name ?? ''}>
                              {c.campaign_name ?? <span className="text-muted">{c.campaign_id.slice(0, 8)}…</span>}
                            </div>
                            <div className="text-[10px] text-muted mt-0.5">
                              {c.status ?? '—'}
                              {c.started_at && <> · started {c.started_at.slice(0, 10)}</>}
                            </div>
                          </td>
                          <td className="text-right tabular-nums">{fmt(c.sent)}</td>
                          <td className="text-right tabular-nums text-ok">{fmt(c.replies_engaged)}</td>
                          <td className="text-right tabular-nums text-watch">{fmt(c.bookings_count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

          </div>

        </div>
      </section>
    </div>
  );
}

// @section 4 Presentational helpers

// @block 4.1 KPI card
function Kpi({ label, value, tone, help }: { label: string; value?: number; tone: string; help: string }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold">{label}</div>
      <div className={`text-3xl md:text-4xl font-bold tabular-nums mt-1 text-${tone}`}>
        {value == null ? '—' : fmt(value)}
      </div>
      <div className="text-[11px] text-muted mt-0.5">{help}</div>
    </div>
  );
}

// @block 4.2 Panel + Row
function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-800 bg-ink-900/60 p-4">
      <div className="text-[10px] uppercase tracking-[2px] text-muted font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-muted">{label}</span>
      <span className={`tabular-nums font-semibold ${tone}`}>{fmt(value)}</span>
    </li>
  );
}

// @section 5 Formatting
function fmt(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}
function shortTs(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}
